import React, { useCallback, useEffect } from 'react';
import { CardViewContentBlockProps } from '../types';
import { Box, Typography } from '@mui/material';
import LibraryCategoryCard from '../../../common/library-category-card/LibraryCategoryCard';
import RegistrationIcon from '../../../../assets/svg/category-registration.svg?react';
import PolicyIcon from '../../../../assets/svg/policy.svg?react';
import OwnershipIcon from '../../../../assets/svg/manual-doc.svg?react';
import SubscriptionIcon from '../../../../assets/svg/box.svg?react';
import OperationsIcon from '../../../../assets/svg/business-to-consumer-plan.svg?react';
import AccountingIcon from '../../../../assets/svg/view-list.svg?react';
import { useTranslation } from 'react-i18next';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import DocumentListVault from '../../document-vault/components/document-list-vault/DocumentListVault';
import styles from '../BusinessProfile.module.scss';
import InsuranceIcon from '../../../../assets/svg/insurance-licenses.svg?react';
import AssetsIcon from '../../../../assets/svg/list_alt_check.svg?react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ProgressOverlay } from '../../../common/progress-overlay/ProgressOverlay';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { RootState } from '../../../store';
import { useDocumentsMetaData } from '../../document-vault/hooks/useDocumentsWorkflowsMetadata';
import { clearDocuments } from '../../../slices/documents/documentsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';

export const BusinessCategoryContent = ({
  documents,
  workflow,
  setWorkflow,
  showWorkflowBreadcrumbs,
  setShowWorkflowBreadcrumbs,
}: CardViewContentBlockProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const workflowItem = searchParams.get('workflow');
  const dispatch = useAppDispatch();
  const documentUploadsLoading = useAppSelector((state: RootState) => state.documents.status);

  // Use metadata hook for business category
  const { uploadedDocumentsMetaData } = useDocumentsMetaData('All', 'business');

  const getDocsFilteredByEmptyWorkflowCount = () => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === '');
    return metaData ? metaData.count : 0;
  };

  const getDocsCount = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData
      ? metaData.count +
          (workflow === t('labels.generalAdministration')
            ? getDocsFilteredByEmptyWorkflowCount()
            : 0)
      : 0;
  };

  const hasNewDocuments = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData ? metaData.isNew : false;
  };

  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.company.registration'):
        return <RegistrationIcon />;
      case t('tagsLabels.documentWorkflow.company.agreements'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.company.management'):
        return <OwnershipIcon />;
      case t('tagsLabels.documentWorkflow.company.subscriptions'):
        return <SubscriptionIcon />;
      case t('tagsLabels.documentWorkflow.company.generalAdministration'):
        return <OperationsIcon />;
      case t('tagsLabels.documentWorkflow.company.accounting'):
        return <AccountingIcon />;
      case t('tagsLabels.documentWorkflow.company.insuranceAndLicenses'):
        return <InsuranceIcon />;
      case t('tagsLabels.documentWorkflow.company.assets'):
        return <AssetsIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.company.registration'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.company.accounting'):
        return '2. ';
      case t('tagsLabels.documentWorkflow.company.agreements'):
        return '3. ';
      case t('tagsLabels.documentWorkflow.company.generalAdministration'):
        return '4. ';
      case t('tagsLabels.documentWorkflow.company.insuranceAndLicenses'):
        return '5. ';
      case t('tagsLabels.documentWorkflow.company.assets'):
        return '6. ';
    }
  };

  const setWorkflowData = useCallback(() => {
    if (workflowItem) {
      setWorkflow(decodeURI(workflowItem));
      setShowWorkflowBreadcrumbs(true);
      navigate(location.pathname);
    }
  }, [workflowItem, navigate, location.pathname, setWorkflow, setShowWorkflowBreadcrumbs]);

  useEffect(() => {
    setWorkflowData();
  }, [setWorkflowData]);

  return (
    <>
      {!workflow && !showWorkflowBreadcrumbs && (
        <Typography
          variant='h3'
          component='h3'
          sx={{ marginBottom: '16px', fontSize: '15px', lineHeight: '24px' }}
        >
          {t('headers.workflows')}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
        }}
      >
        {workflow && showWorkflowBreadcrumbs ? (
          <>
            <Box className={styles.breadcrumbs}>
              <Typography
                variant='h3'
                component='h3'
                className={styles.documentViewText}
                onClick={() => {
                  setShowWorkflowBreadcrumbs(false);
                  setWorkflow(null);
                  dispatch(clearDocuments());
                }}
              >
                {t('headers.workflows')}
              </Typography>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              <Typography variant='body2' className={styles.documentViewText}>
                {workflow === t('headers.general') ? t('labels.generalUnassigned') : workflow}
              </Typography>
            </Box>
            <Box position='relative' width='100%'>
              <DocumentListVault
                documents={[...documents] /*getDocsFilteredByWorkflow(workflow)*/}
              />
              {documentUploadsLoading === 'loading' && (
                <ProgressOverlay className={'position-absolute'} />
              )}
            </Box>
          </>
        ) : (
          <>
            {TAGS_LABELS.documentWorkflow.business.map((el) => (
              <LibraryCategoryCard
                title={getWorkflowNumber(t(el)) + t(el)}
                description={
                  getDocsCount(t(el)) +
                  ' document' +
                  (getDocsCount(t(el)) === 0 || getDocsCount(t(el)) > 1 ? 's' : '')
                }
                onClick={() => {
                  setWorkflow(t(el));
                  setShowWorkflowBreadcrumbs(true);
                }}
                icon={getIcon(t(el))}
                key={el}
                hasNewLabel={
                  el === 'tagsLabels.documentWorkflow.company.generalAdministration'
                    ? hasNewDocuments(t(el)) || hasNewDocuments('')
                    : hasNewDocuments(t(el))
                }
              />
            ))}
          </>
        )}
      </Box>
    </>
  );
};
